/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (parses a rich-text string for inline component tags
 *              and interleaves them with plain-text parts)
 *   extends:   Component (@glimmer/component)
 *   template:  rich-text-with-components.hbs
 *   args:      @text
 *   uses:      -
 *   siblings:  -
 *   tests:     storefront-components-test.js
 *   gotchas:   only tags listed in the local COMPONENT_MAP
 *              (AssicurazioneSnowCare, AssicurazioneAostaValley) resolve — any
 *              other self-closing tag is emitted back as literal text.
 *
 * `parts` splits @text on `<Tag @arg={{value}} .../>`-style markers, mapping
 * recognized tags to a { type: 'component', name, args } entry for the
 * template to render, and everything else to { type: 'text', value }.
 */
import Component from '@glimmer/component';

const COMPONENT_MAP = [
  { tag: 'AssicurazioneSnowCare', component: 'assicurazione-snow-care' },
  { tag: 'AssicurazioneAostaValley', component: 'assicurazione-aosta-valley' },
];

export default class RichTextWithComponentsComponent extends Component {
  get parts() {
    const text = this.args.text ?? '';
    const parts = [];

    // Regex aggiornata per gestire spazi e newline negli attributi
    const COMPONENT_REGEX_NEW = /<(\w+)\s+((?:@\w+=\{\{[^}]+\}\}\s*)*)\s*\/>/gs;

    const matches = Array.from(text.matchAll(COMPONENT_REGEX_NEW));

    let lastIndex = 0;

    for (const match of matches) {
      const fullMatch = match[0];
      const tag = match[1];
      const attrs = match[2];
      const index = match.index;

      // Testo prima del componente
      const before = text.slice(lastIndex, index);
      if (before) parts.push({ type: 'text', value: before });

      // Trova mapping Ember
      const comp = COMPONENT_MAP.find((c) => c.tag === tag);
      if (!comp) {
        parts.push({ type: 'text', value: fullMatch });
        lastIndex = index + fullMatch.length;
        continue;
      }

      // Parsiamo gli argomenti con regex più flessibile
      const args = {};
      const ARG_REGEX_NEW = /@(\w+)=\{\{([^}]+)\}\}/g;
      const argMatches = Array.from(attrs.matchAll(ARG_REGEX_NEW));

      for (const argMatch of argMatches) {
        const argName = argMatch[1];
        const rawValue = argMatch[2].trim();

        let value;
        if (rawValue === 'true') value = true;
        else if (rawValue === 'false') value = false;
        else if (!isNaN(rawValue)) value = Number(rawValue);
        else value = rawValue;

        args[argName] = value;
      }

      parts.push({ type: 'component', name: comp.component, args });

      lastIndex = index + fullMatch.length;
    }

    // Testo rimanente
    const after = text.slice(lastIndex);
    if (after) parts.push({ type: 'text', value: after });

    return parts;
  }
}
